<template>
  <div class="product-media" :class="{ 'product-media--large-media': isLargeMedia }">
    <div class="product-media__media-container">
      <div class="product-media__actions">
        <button v-if="mq('medium') && !isLargeMedia" v-enlarge-media class="product-media__enlarge">
          <oie-use-svg svg="zoom"></oie-use-svg>
        </button>

        <button class="product-media__measure-focus-button">
          {{ tStatic('measurementLink') }}
        </button>
      </div>

      <button v-enlarge-media class="product-media__close-enlarge">
        {{ tStatic('closeLink') }} <oie-use-svg svg="close"></oie-use-svg>
      </button>

      <button v-if="mediaAssets.length > 1" class="product-media__carousel-prev js-prev-slide">
        <oie-use-svg svg="circle-caret"></oie-use-svg>
      </button>

      <button v-if="mediaAssets.length > 1" class="product-media__carousel-next js-next-slide">
        <oie-use-svg svg="circle-caret"></oie-use-svg>
      </button>

      <div class="product-media__carousel">
        <div
          id="product-carousel"
          v-product-slider
          @index-changed="pauseVideo">
          <div v-for="(item, i) in mediaAssets" :key="`product-media__${i}`">
            <div v-if="item.type === 'orbitvu'" :class="['product-media__orbitvu-viewer', { 'product-media__orbitvu-viewer--is-large': isLargeMedia }]">
              <div :id="'ovContent-'+ product.videoAssetUrl" class="orbitvu-container"></div>
            </div>

            <oie-base-image
              v-else-if="item.type === 'image'"
              v-enlarge-media
              :images="getImageUrl(item.imageUrl)"
              :alt-text="altText"
              class="product-media__image"></oie-base-image>

            <oie-video-player
              v-else-if="item.type === 'video'"
              :vimeo-id="item.vendor === 'vimeo' ? item.id : ''"
              :youtube-id="item.vendor === 'youtube' ? item.id : ''"
              :poster-image="item.imageUrl"></oie-video-player>

            <oie-base-image
              v-else
              :images="`${getAssetPath('/globalassets/missing404/404.png')}?preset=ProductLarge`"
              alt-text="No image found"
              class="product-media__image"></oie-base-image>
          </div>
        </div>
      </div>
    </div>

    <div id="slider-thumbs" :class="['product-media__thumbs', { 'product-media__thumbs--large-media': isLargeMedia }]">
      <div v-for="(item, i) in mediaAssets" :key="`product-media__thumb-${i}`" class="product-media__thumb">
        <oie-base-image
          :images="item.type === 'orbitvu' ? item.imageUrl : `${getAssetPath(item.imageUrl)}?preset=ProductSmall&watermark=OIEWatermarkSmall`"
          :alt-text="altText"
          class="product-media__thumb-image">
        </oie-base-image>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'
export default {
  props: {
    product: {
      type: Object,
      required: false,
      default: null
    },
    images: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data: () => ({
    largeMediaSize: 740
  }),
  computed: {
    ...mapGetters('ui', ['isLargeMedia']),
    imageUrls() {
      return this.product ? this.product.mediaAssetsUrl : this.images
    },
    altText() {
      return this.product ? `${this.product.itemName} - ${this.product.itemNameSpecification}` : 'Product image'
    },
    mediaAssets() {
      const items = []

      if (this.isVideoAsset) {
        items.push({ type: 'orbitvu', id: this.product.videoAssetUrl, imageUrl: `//static.orbitvu.co/001/${this.product.videoAssetUrl}/iproc/600/600/img01.png` })
      }
      _.forEach(this.imageUrls, (item) => {
        items.push({ type: 'image', imageUrl: item })
      })
      if(this.hasVimeoYoutubeAsset) {
        items.push({ type: 'video', ...this.product.vimeoYoutubeAsset })
      }
      return items
    },
    isVideoAsset() {
      return this.product && this.product.videoAssetUrl
    },
    hasVimeoYoutubeAsset() {
      return this.product && this.product.vimeoYoutubeAsset
    }
  },
  beforeMount() {
    if(this.isVideoAsset) {
      this.mountOrbitvuScript()
    }
  },
  methods: {
    mountOrbitvuScript() {
      const orbitvu = document.createElement('script')
      orbitvu.setAttribute(
        'src',
        '//orbitvu.co/001/' + this.product.videoAssetUrl + '/ov3601/3/script?width=auto&content2=yes&height=auto&teaser=autorotate'
      )
      document.head.appendChild(orbitvu)
    },
    pauseVideo() {
      this.publish('pause-video')
    },
    getImageUrl(url) {
      if(!url) {
        return `${this.getAssetPath('/globalassets/missing404/404.png')}?preset=ProductSmall`
      } else {
        return !this.isLargeMedia ? `${this.getAssetPath(url)}?preset=ProductLarge&watermark=OIEWatermarkLarge` : `${this.getAssetPath(url)}?height=${this.largeMediaSize}&watermark=OIEWatermarkMaximum`
      }
    }
  }
}
</script>

<style lang="scss">
@import './node_modules/tiny-slider/src/tiny-slider';

.product-media {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: measure(half);
  // background-color: deepskyblue;

  @include media(medium) {
    flex-direction: row;
    margin-right: measure(full);
    margin-bottom: 0;
    padding-top: measure(half);
  }
}

.product-media--large-media {
  padding-bottom: measure(half);
  border-bottom: 1px solid color(border-gray);
}

.product-details__top--large-media .product-media {
  flex-direction: column;
  max-width: initial;

  @include media(medium) {
    margin-right: 0;
    margin-bottom: measure(full);
  }
}

.product-media__media-container {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  @include media(medium) {
    order: 2;
    margin-left: measure(full);
  }
}

.product-details__top--large-media .product-media__media-container {
  order: 0;
  margin-bottom: measure(full);
  margin-left: 0;
}

.product-media__carousel {
  max-width: 22.375rem;
  margin: 0 auto measure(half) auto;
  overflow: hidden;

  &:hover {
    cursor: pointer;
  }

  @include media(medium) {
    margin: 0 auto;
  }
}

.product-media__carousel .tns-horizontal.tns-subpixel > .tns-item {
  vertical-align: middle;
}

.product-details__top--large-media .product-media__carousel {
  max-width: 70%;
}

.product-media__image {
  display: block;
  max-width: 100%;
  width: 100%;
  max-height: 100%;
  margin: 0 auto;
}

.product-details__top--large-media .product-media__image {
  width: auto;
  max-height: 46.25rem;
}

.product-media__actions {
  position: absolute;
  top: 0;
  right: 0;
}

.product-details__top--large-media .product-media__actions {
  right: 0;
  justify-content: center;
}

.product-media__enlarge {
  @include body-smaller;
  @include reset-button;
  margin-right: measure(half);
  z-index: 1;
}

.product-media__enlarge > svg {
  margin-right: measure(eighth);
}

.product-details__top--large-media .product-media__enlarge {
  display: none;
}

.product-media__measure-focus-button {
  @include reset-button;
  display: none;
}

.product-media__close-enlarge {
  position: absolute;
  top: 0;
  right: 0;
  display: none;
  align-items: center;
  height: 2.625rem;
  background-color: transparent;
  border: none;
  cursor: pointer;
  z-index: 1;
}

.product-media__close-enlarge svg {
  width: 2.625rem;
  height: 2.625rem;
  margin-left: measure(quarter);
  color: color(gray-3);
}

.product-details__top--large-media .product-media__close-enlarge {
  display: flex;
}

.product-media__carousel-next,
.product-media__carousel-prev {
  position: absolute;
  top: 50%;
  right: 0;
  display: none;
  align-items: center;
  justify-content: center;
  width: 2.625rem;
  height: 2.625rem;
  padding: 0;
  border: none;
  background-color: transparent;
  transform: translateY(-50%);
  z-index: 1;

  &:hover {
    cursor: pointer;
  }
}

.product-media__carousel-next svg,
.product-media__carousel-prev svg {
  width: 100%;
  height: 100%;
  color: color(gray-3);
}

.product-media__media-container:hover .product-media__carousel-next,
.product-media__media-container:hover .product-media__carousel-prev {
  @include media(small) {
    display: flex;
  }
}

.product-details__top--large-media .product-media__carousel-next,
.product-details__top--large-media .product-media__carousel-prev {
  display: flex;
}

.product-media__carousel-prev {
  left: 0;
}

.product-media__carousel-prev svg {
  transform: scale(-1);
}

.product-media__thumbs {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: -#{measure(eighth)};
  margin-left: -#{measure(eighth)};

  @include media(medium) {
    flex-direction: column;
    flex-wrap: nowrap;
  }
}

.product-media__thumbs--large-media {
  flex-direction: row;
  justify-content: center;
}

.product-media__thumb {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 3.9375rem;
  height: 3.9375rem;
  margin-top: measure(eighth);
  margin-left: measure(eighth);
  padding: measure(eighth);
  border: 1px solid color(border-gray);
  outline: none;
  cursor: pointer;

  @include media(medium) {
    margin-left: 0;
    margin-top: measure(eighth);
  }
}

.product-media__thumbs--large-media .product-media__thumb {
  margin-left: measure(eighth);
  margin-top: 0;
}

.product-media__thumb.tns-nav-active {
  border-color: color(border-blue-1);
}

.product-media__thumb-image {
  width: 100%;
  height: 100%;
}

.product-media__thumb--is-selected {
  border-color: color(border-blue-1);

}

.product-media__thumb-image-svg {
  position: absolute;
  top: 0;
  right: 0;
  width: 1rem;
  height: 1rem;
}

.product-media__orbitvu-viewer {
  width: 16.25rem;
  height: 16.25rem;
  background-color: color(background-white);
  background-repeat: no-repeat;
  background-position: center center;
}

.product-media__orbitvu-viewer--is-large {
  width: 100%;
  height: 46.25rem;
}
</style>
